// Register.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import TurnstileWidget from '../components/TurnstileWidget';
import { getDeviceType, getDeviceUUID } from '../utils/deviceManager';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    nom: '',
    prenom: '',
    entreprise: '',
    telephone: '',
    password: '',
    confirmPassword: '',
    license_key: '',
    activite: '',
    cguAccepted: false,
    cgvAccepted: false,
  });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [showCGVModal, setShowCGVModal] = useState(false);
  const [cguViewed, setCguViewed] = useState(false);
  const [cgvViewed, setCgvViewed] = useState(false);
  const [cguScrolledToBottom, setCguScrolledToBottom] = useState(false);
  const [cgvScrolledToBottom, setCgvScrolledToBottom] = useState(false);
  const [cguText, setCguText] = useState('');
  const [cgvText, setCgvText] = useState('');
  
  const cguContentRef = useRef(null);
  const cgvContentRef = useRef(null);
  
  const navigate = useNavigate();
  
  // Fetch CGU and CGV text
  useEffect(() => {
    const fetchCGU = async () => {
      try {
        const response = await fetch('/cgu.html');
        const text = await response.text();
        setCguText(text);
      } catch (err) {
        console.error('Error fetching CGU:', err);
        setCguText('<p>Erreur lors du chargement des CGU.</p>');
      }
    };
    
    const fetchCGV = async () => {
      try {
        const response = await fetch('/cgv.html');
        const text = await response.text();
        setCgvText(text);
      } catch (err) {
        console.error('Error fetching CGV:', err);
        setCgvText('<p>Erreur lors du chargement des CGV.</p>');
      }
    };
    
    fetchCGU();
    fetchCGV();
  }, []);
  
  // Handle scrolling in CGU/CGV modals
  const handleCguScroll = () => {
    if (cguContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = cguContentRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setCguScrolledToBottom(true);
      }
    }
  };
  
  const handleCgvScroll = () => {
    if (cgvContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = cgvContentRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setCgvScrolledToBottom(true);
      }
    }
  };
  
  // Validation du mot de passe
  const validatePassword = (password, confirmPassword) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return regex.test(password) && password === confirmPassword;
  };
  
  // Validation du formulaire complet
  const validateForm = () => {
    const errors = {};
    
    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = "Format d'email invalide";
    }
    
    // Validation du téléphone - exactement 10 chiffres
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.telephone)) {
      errors.telephone = "Le numéro de téléphone doit contenir exactement 10 chiffres";
    }
    
    // Validation des champs obligatoires
    if (!formData.nom.trim()) errors.nom = "Le nom est requis";
    if (!formData.prenom.trim()) errors.prenom = "Le prénom est requis";
    if (!formData.entreprise.trim()) errors.entreprise = "L'entreprise est requise";
    if (!formData.license_key.trim()) errors.license_key = "Le code d'activation est requis";
    if (!formData.activite.trim()) errors.activite = "L'activité est requise";
    
    // Validation du mot de passe
    if (!validatePassword(formData.password, formData.confirmPassword)) {
      errors.password = "Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial";
    }
    
    // Validation des CGU et CGV
    if (!formData.cguAccepted) {
      errors.cguAccepted = "Vous devez accepter les CGU";
    }
    
    if (!formData.cgvAccepted) {
      errors.cgvAccepted = "Vous devez accepter les CGV";
    }
    
    // Si les CGU n'ont pas été consultées
    if (!cguViewed) {
      errors.cguViewed = "Vous devez consulter les CGU avant de les accepter";
    }
    
    // Si les CGV n'ont pas été consultées
    if (!cgvViewed) {
      errors.cgvViewed = "Vous devez consulter les CGV avant de les accepter";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!turnstileToken) {
      setError("Veuillez compléter la vérification Turnstile.");
      return;
    }
    
    // Validation complète du formulaire
    if (!validateForm()) {
      return;
    }
    
    // Préparer les données à envoyer avec l'email comme login
    const dataToSend = {
      ...formData,
      login: formData.email // Utiliser l'email comme login
    };
    delete dataToSend.confirmPassword;
    
    try {
      await API.post('/api/auth/register', dataToSend, {
        headers: {
          'X-Turnstile-Token': turnstileToken
        }
      });
      alert("Inscription réussie !");
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'inscription.");
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Effacer l'erreur spécifique quand l'utilisateur commence à corriger
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Ouvrir la modal CGU
  const openCGUModal = () => {
    setShowCGUModal(true);
  };
  
  // Fermer la modal CGU et marquer comme vue
  const closeCGUModal = () => {
    setShowCGUModal(false);
    setCguViewed(true);
    setFormData(prev => ({
      ...prev,
      cguAccepted: true
    }));
  };
  
  // Ouvrir la modal CGV
  const openCGVModal = () => {
    setShowCGVModal(true);
  };
  
  // Fermer la modal CGV et marquer comme vue
  const closeCGVModal = () => {
    setShowCGVModal(false);
    setCgvViewed(true);
    setFormData(prev => ({
      ...prev,
      cgvAccepted: true
    }));
  };

return (
  <div className="register-container">
    <h2>Inscription - Première Connexion</h2>
    {error && <p className="error">{error}</p>}
    <form onSubmit={handleSubmit} className="register-form">
      {/* First row: Nom and Prénom side by side */}
      <div className="form-row">
        <div className="form-column">
          <div className="form-group">
            <label htmlFor="nom">Nom</label>
            <input 
              id="nom"
              name="nom" 
              placeholder="Nom" 
              value={formData.nom} 
              onChange={handleChange} 
              required 
            />
            {fieldErrors.nom && <p className="error">{fieldErrors.nom}</p>}
          </div>
        </div>
        
        <div className="form-column">
          <div className="form-group">
            <label htmlFor="prenom">Prénom</label>
            <input 
              id="prenom"
              name="prenom" 
              placeholder="Prénom" 
              value={formData.prenom} 
              onChange={handleChange} 
              required 
            />
            {fieldErrors.prenom && <p className="error">{fieldErrors.prenom}</p>}
          </div>
        </div>
      </div>
      
      {/* Email field */}
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input 
          id="email"
          name="email" 
          type="email" 
          placeholder="Email" 
          value={formData.email} 
          onChange={handleChange} 
          required 
        />
        {fieldErrors.email && <p className="error">{fieldErrors.email}</p>}
      </div>
      
      {/* Telephone field */}
      <div className="form-group">
        <label htmlFor="telephone">Téléphone</label>
        <input 
          id="telephone"
          name="telephone" 
          placeholder="Téléphone (10 chiffres)" 
          value={formData.telephone} 
          onChange={handleChange} 
          required 
        />
        {fieldErrors.telephone && <p className="error">{fieldErrors.telephone}</p>}
      </div>
      
      {/* Password and Confirm Password side by side */}
      <div className="form-row">
        <div className="form-column">
          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <input 
              id="password"
              name="password" 
              type="password" 
              placeholder="Mot de passe" 
              value={formData.password} 
              onChange={handleChange} 
              required 
            />
          </div>
        </div>
        
        <div className="form-column">
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
            <input 
              id="confirmPassword"
              name="confirmPassword" 
              type="password" 
              placeholder="Confirmer le mot de passe" 
              value={formData.confirmPassword} 
              onChange={handleChange} 
              required 
            />
            {fieldErrors.password && <p className="error">{fieldErrors.password}</p>}
          </div>
        </div>
      </div>
      
      {/* Entreprise field */}
      <div className="form-group">
        <label htmlFor="entreprise">Entreprise</label>
        <input 
          id="entreprise"
          name="entreprise" 
          placeholder="Entreprise" 
          value={formData.entreprise} 
          onChange={handleChange} 
          required 
        />
        {fieldErrors.entreprise && <p className="error">{fieldErrors.entreprise}</p>}
      </div>
      
      {/* Activité field */}
      <div className="form-group">
        <label htmlFor="activite">Activité</label>
        <input 
          id="activite"
          name="activite" 
          placeholder="Activité" 
          value={formData.activite} 
          onChange={handleChange} 
          required 
        />
        {fieldErrors.activite && <p className="error">{fieldErrors.activite}</p>}
      </div>
      
      {/* Code d'activation field */}
      <div className="form-group">
        <label htmlFor="license_key">Code d'activation</label>
        <input 
          id="license_key"
          name="license_key" 
          placeholder="Code d'activation" 
          value={formData.license_key} 
          onChange={handleChange} 
          required 
        />
        {fieldErrors.license_key && <p className="error">{fieldErrors.license_key}</p>}
      </div>
      
      {/* Keep the rest of the form as is */}
      <div className="terms-buttons-container">
        <button 
          type="button" 
          className="cgu-button left-btn" 
          onClick={openCGUModal}
        >
          Lire les CGU
        </button>
        <button 
          type="button" 
          className="cgu-button right-btn" 
          onClick={openCGVModal}
        >
          Lire les CGV
        </button>
      </div>
      
      {(fieldErrors.cguViewed || fieldErrors.cgvViewed) && (
        <div className="terms-error">
          {fieldErrors.cguViewed && <p className="error">{fieldErrors.cguViewed}</p>}
          {fieldErrors.cgvViewed && <p className="error">{fieldErrors.cgvViewed}</p>}
        </div>
      )}
      
      <div className="terms-checkbox-container">
        <label className="cgu-label left-checkbox">
          <input 
            name="cguAccepted" 
            type="checkbox" 
            checked={formData.cguAccepted} 
            onChange={handleChange}
            disabled={!cguViewed}
            required 
          />
          J'accepte les CGU
        </label>
        <label className="cgu-label right-checkbox">
          <input 
            name="cgvAccepted" 
            type="checkbox" 
            checked={formData.cgvAccepted} 
            onChange={handleChange}
            disabled={!cgvViewed}
            required 
          />
          J'accepte les CGV
        </label>
      </div>
      
      {(fieldErrors.cguAccepted || fieldErrors.cgvAccepted) && (
        <div className="terms-error">
          {fieldErrors.cguAccepted && <p className="error">{fieldErrors.cguAccepted}</p>}
          {fieldErrors.cgvAccepted && <p className="error">{fieldErrors.cgvAccepted}</p>}
        </div>
      )}
      
      <div className="turnstile">
        <TurnstileWidget onVerify={setTurnstileToken} />
      </div>
      
      <button type="submit" className="submit-button">S'inscrire</button>
    </form>

      {/* Modal CGU */}
      {showCGUModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Conditions Générales d'Utilisation</h3>
            <div 
              className="cgu-content" 
              ref={cguContentRef}
              onScroll={handleCguScroll}
              style={{maxHeight: "300px", overflowY: "auto", margin: "20px 0"}}
              dangerouslySetInnerHTML={{ __html: cguText }}
            >
            </div>
            <button 
              className="modal-close-btn" 
              onClick={closeCGUModal}
              disabled={!cguScrolledToBottom}
            >
              {cguScrolledToBottom ? 
                "J'ai lu et j'accepte les CGU" : 
                "Veuillez lire les CGU jusqu'à la fin"}
            </button>
          </div>
        </div>
      )}
      
      {/* Modal CGV */}
      {showCGVModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Conditions Générales de Vente</h3>
            <div 
              className="cgu-content" 
              ref={cgvContentRef}
              onScroll={handleCgvScroll}
              style={{maxHeight: "300px", overflowY: "auto", margin: "20px 0"}}
              dangerouslySetInnerHTML={{ __html: cgvText }}
            >
            </div>
            <button 
              className="modal-close-btn" 
              onClick={closeCGVModal}
              disabled={!cgvScrolledToBottom}
            >
              {cgvScrolledToBottom ? 
                "J'ai lu et j'accepte les CGV" : 
                "Veuillez lire les CGV jusqu'à la fin"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
export default Register;
